<template>
  <section class="page-panel general-invitees-page">
    <div class="general-invitees-page__header grid grid-gap-16 align-items-center">
      <h2 class="col-12">{{ pageTitle }}</h2>
    </div>

    <Filters
      v-model="state.filters"
      :filter-fields="filterFields"
      @change="resetPageOnFilterChange"
    />

    <v-data-table-server
      v-model:expanded="state.expandedInviteeEmails"
      class="table table--framed general-invitees-table"
      :headers="tableHeaders"
      :items="state.invitees"
      :items-length="state.totalResults"
      :items-per-page="state.itemsPerPage"
      item-value="email"
      :mobile="false"
      :page="state.currentPage"
      :hide-default-header="isMobileTable"
      show-expand
      hide-default-footer
    >
      <template #item="{ item, internalItem, isExpanded, toggleExpand }">
        <tr>
          <td class="table__person-cell">
            <span class="table__primary-text">{{ item.fullName }}</span>
            <span class="table__secondary-text is-hidden-sm-up">{{ item.email }}</span>
          </td>
          <td class="is-hidden-sm-down">{{ item.company }}</td>
          <td class="is-hidden-sm-down">{{ item.email }}</td>
          <td>
            <v-btn
              :aria-expanded="isExpanded(internalItem)"
              :aria-label="isExpanded(internalItem) ? 'Zatvori događaje uzvanika' : 'Prikaži događaje uzvanika'"
              class="button table__actions-button"
              :icon="isExpanded(internalItem) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              size="small"
              variant="text"
              @click="toggleExpand(internalItem)"
            />
          </td>
        </tr>
      </template>

      <template #expanded-row="{ columns, item }">
        <tr>
          <td
            class="general-invitees-table__expanded-cell"
            :colspan="isMobileTable ? 2 : columns.length"
          >
            <InviteeAttendedEvents
              :candidate-id="item.id"
              :events="item.events"
              :return-query="inviteeListReturnQuery"
            />
          </td>
        </tr>
      </template>
    </v-data-table-server>

    <Pagination
      :page="state.currentPage"
      :per-page="state.itemsPerPage"
      :total-results="state.totalResults"
      @page-change="state.currentPage = $event"
    />
  </section>
</template>

<script setup>
import { computed, onWatcherCleanup, reactive, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'

import Filters from '../../components/Filters.vue'
import InviteeAttendedEvents from '../../components/InviteeAttendedEvents.vue'
import Pagination from '../../components/Pagination.vue'
import { apiClient } from '../../services/apiClient'
import { showRequestErrorToast } from '../../store/appStore'

const route = useRoute()
const { mobile: isMobileTable } = useDisplay({ mobileBreakpoint: 768 })
const state = reactive({
  filters: {
    search: route.query.search ?? null,
  },
  currentPage: Math.max(1, Number(route.query.page) || 1),
  itemsPerPage: 10,
  expandedInviteeEmails: [],
  invitees: [],
  totalResults: 0,
})

const pageTitle = computed(() => route.meta.title)
const inviteeListReturnQuery = computed(() => ({
  ...(state.currentPage > 1 ? { page: state.currentPage } : {}),
  ...(state.filters.search?.trim() ? { search: state.filters.search.trim() } : {}),
}))

const filterFields = [
  {
    key: 'search',
    type: 'search',
    placeholder: 'Pretraži po imenu, tvrtki ili e-mailu...',
    width: '100%',
  },
]

const tableHeaders = [
  { title: 'Ime i prezime', key: 'fullName', sortable: false, width: '35%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Tvrtka', key: 'company', sortable: false, width: '30%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'E-mail', key: 'email', sortable: false, width: '35%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
]

watch(
  [() => state.currentPage, () => state.filters.search],
  getInviteesAsync,
  { immediate: true }
)

function resetPageOnFilterChange () {
  state.currentPage = 1
}

async function getInviteesAsync () {
  let isCurrentRequest = true
  onWatcherCleanup(() => { isCurrentRequest = false })

  state.invitees = []

  try {
    const candidateListData = await apiClient.getAsync('/api/candidates', {
      queryParams: {
        page: state.currentPage,
        perPage: state.itemsPerPage,
        search: state.filters.search?.trim() || null,
      },
    })

    if (!isCurrentRequest) {
      return
    }

    state.invitees = candidateListData.results
    state.totalResults = candidateListData.pagination.totalResults

    const expandedInvitee = state.invitees.find(
      ({ id }) => String(id) === String(route.query.expandedInviteeId)
    )

    state.expandedInviteeEmails = expandedInvitee ? [expandedInvitee.email] : []
  } catch (error) {
    console.error('Failed to load invitees.', error)
    if (isCurrentRequest) {
      showRequestErrorToast(error, 'Uzvanike nije bilo moguće učitati.')
    }
  }
}
</script>

<style
  scoped
  lang="scss"
>
.general-invitees-page {
  @include breakpoint-up("sm") {
    min-height: 1233px;
  }

  &__header {
    margin-bottom: clamp-bp("sm", "xl", 20, 30);
  }
}

.general-invitees-table {
  --table-min-width: 100%;
  --table-actions-width: 52px;

  margin-top: clamp-bp("sm", "xl", 20, 30);

  &__expanded-cell {
    width: auto !important;
    padding: 0 !important;
    background: rgba(230, 233, 237, 0.3);
    text-align: left !important;
  }
}
</style>
